import { Button, Modal } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { nullSafeGetOrElse, nullSafeGet, getCurrency } from '../../utils/DataAccessUtils';
import GeneralStatusDisplay from '../AdminCycleEventsIndexPage/GeneralStatusDisplay';
import { ACC_AUDIT_KEY_DISPLAY_NAMES } from '../../utils/AssetCycleUtils';
import { ASSET_FIELDS_LABELS_MAP, ASSET_FIELDS_DETAILS } from '../../utils/assetUtils';
import { getProtectedImageUriGeneric } from '../../utils/FileAccessUtils';
import { CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import ScannedAssetDataSection from './ScannedAssetDataSection';
import SeparatorWithText from '../common/SeperatorWithText';
import { withUserType } from '../withUserType';

const ACTION_LABELS = {
	assignQRCodeAndRemoveFromMatch: 'Mark to assign QR & remove from match',
	swapQRCodes: 'Swapped QR codes',
	applyNewQRCode: 'Applied new QR code',
	needsFurtherReview: 'Needs further review',
	qrCodeRemoved: 'QR code removed',
};

function AssetAuditReadOnlyModal({ assetAudit, cycleRun, onClose, userType, currentUser }) {
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewImage, setPreviewImage] = useState('');

	const currency = useMemo(() => getCurrency({ currentUser }), [currentUser]);

	const capturedFields = useMemo(
		() => nullSafeGetOrElse('capturedFields', assetAudit, []),
		[assetAudit]
	);

	const captureFields = useMemo(
		() => nullSafeGetOrElse('assetCycleCountEvent.captureFields', cycleRun, []),
		[cycleRun]
	);

	const nonImageCaptureFields = useMemo(
		() =>
			captureFields.filter((field) => {
				const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
				const fieldType = nullSafeGetOrElse('type', fieldDetails, 'text');
				return fieldType !== 'images';
			}),
		[captureFields]
	);

	const capturedFieldsMap = useMemo(
		() =>
			capturedFields.reduce((acc, field) => {
				acc[field.key] = field.value;
				return acc;
			}, {}),
		[capturedFields]
	);

	const capturedCount = useMemo(() => {
		return nonImageCaptureFields.filter((field) => {
			const value = capturedFieldsMap[field.key];
			return value !== undefined && value !== null && value !== '';
		}).length;
	}, [nonImageCaptureFields, capturedFieldsMap]);

	const formatValue = (value, fieldKey) => {
		const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === fieldKey);
		const fieldType = nullSafeGetOrElse('type', fieldDetails, 'text');
		if (value === undefined || value === null || value === '') return '--';
		switch (fieldType) {
			case 'date':
				return moment(value).format('D MMM, YYYY');
			case 'currency':
				return currency ? currency.format(value) : value;
			case 'switch':
				return value ? 'Yes' : 'No';
			default:
				return String(value);
		}
	};

	const toImageUris = (images) => {
		if (!images || !Array.isArray(images)) return [];
		return images
			.filter((img) => img.fileId && img.fileName)
			.map((img) => ({
				uri: getProtectedImageUriGeneric(img.fileId, img.fileName),
				fileName: img.fileName,
			}));
	};

	const qrCodeImages = useMemo(
		() => toImageUris(nullSafeGetOrElse('qrCodeImages', assetAudit, [])),
		[assetAudit]
	);
	const serialNumberImages = useMemo(
		() => toImageUris(nullSafeGetOrElse('serialNumberImages', assetAudit, [])),
		[assetAudit]
	);
	const assetImages = useMemo(
		() => toImageUris(nullSafeGetOrElse('assetImages', assetAudit, [])),
		[assetAudit]
	);
	const dataPlateImages = useMemo(
		() => toImageUris(nullSafeGetOrElse('dataPlateImages', assetAudit, [])),
		[assetAudit]
	);

	const hasQrCodeImages = captureFields.some((f) => f.key === 'qrCodeImages');
	const hasSerialNumberImages = captureFields.some((f) => f.key === 'serialNumberImages');
	const hasAssetImages = captureFields.some((f) => f.key === 'assetImages');
	const hasDataPlateImages = captureFields.some((f) => f.key === 'dataPlateImages');

	const needsVerificationLabel = nullSafeGet('needsVerificationAction', assetAudit)
		? ACTION_LABELS[assetAudit.needsVerificationAction]
		: null;

	const renderImageSection = (label, images) => {
		if (!images || images.length === 0) {
			return (
				<div className="mb-4">
					<div className="bodymedium mb-2">{label}:</div>
					<div className="bodyregular text-[#00000073]">--</div>
				</div>
			);
		}
		return (
			<div className="mb-4">
				<div className="bodymedium mb-2">{label}:</div>
				<div className="flex flex-row gap-2">
					{images.map((img, idx) => (
						<div
							key={idx}
							className="cursor-pointer overflow-hidden rounded"
							style={{ width: 80, height: 80 }}
							onClick={() => {
								setPreviewImage(img.uri);
								setPreviewVisible(true);
							}}
						>
							<img
								src={img.uri}
								alt={`${label} ${idx + 1}`}
								style={{ width: '100%', height: '100%', objectFit: 'cover' }}
							/>
						</div>
					))}
				</div>
			</div>
		);
	};

	return (
		<>
			<Modal
				title={
					<div className="flex flex-col gap-2">
						<div className="h4medium">
							{nullSafeGetOrElse('asset.name', assetAudit, '')},{' '}
							{nullSafeGetOrElse('asset.assetType.name', assetAudit, '')}
						</div>
						<div className="flex flex-row items-center gap-2">
							<GeneralStatusDisplay
								statusKey={assetAudit.matchStatus}
								statusLabel={nullSafeGetOrElse(
									assetAudit.matchStatus,
									ACC_AUDIT_KEY_DISPLAY_NAMES,
									assetAudit.matchStatus
								)}
							/>
							{needsVerificationLabel ? (
								<span className="footnoteMedium text-[#DB8556]">{needsVerificationLabel}</span>
							) : null}
						</div>
					</div>
				}
				visible
				onCancel={onClose}
				bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
				footer={
					<div className="flex flex-row items-center justify-between">
						<span className="flex items-center gap-1" style={{ color: '#389E0D' }}>
							<CheckCircleOutlined /> Changes Applied
						</span>
						<Button type="primary" size="large" onClick={onClose}>
							Done
						</Button>
					</div>
				}
			>
				<ScannedAssetDataSection assetAudit={assetAudit} userType={userType} />

				<div className="mt-4 flex flex-col">
					<span className="h5medium">
						Fields Captured ({capturedCount}/{nonImageCaptureFields.length}):
					</span>
					<SeparatorWithText className="mb-3 mt-2" />
					<div className="flex flex-col gap-2">
						{nonImageCaptureFields.map((field) => (
							<div
								key={field.key}
								className="flex flex-row items-center gap-2 text-character-primary"
							>
								<span className="bodyregular" style={{ minWidth: 140 }}>
									{ASSET_FIELDS_LABELS_MAP[field.key] || field.key}:
								</span>
								<span className="bodymedium">
									{formatValue(capturedFieldsMap[field.key], field.key)}
								</span>
							</div>
						))}
					</div>
				</div>

				{hasQrCodeImages
					? renderImageSection(
							ASSET_FIELDS_LABELS_MAP['qrCodeImages'] || 'QR Code Image',
							qrCodeImages
					  )
					: null}
				{hasSerialNumberImages
					? renderImageSection(
							ASSET_FIELDS_LABELS_MAP['serialNumberImages'] || 'Barcode Image',
							serialNumberImages
					  )
					: null}
				{hasAssetImages
					? renderImageSection(
							ASSET_FIELDS_LABELS_MAP['assetImages'] || 'Asset Images',
							assetImages
					  )
					: null}
				{hasDataPlateImages
					? renderImageSection(
							ASSET_FIELDS_LABELS_MAP['dataPlateImages'] || 'Data Plate Image',
							dataPlateImages
					  )
					: null}
			</Modal>
			<Modal visible={previewVisible} footer={null} onCancel={() => setPreviewVisible(false)}>
				<img alt="preview" style={{ width: '100%' }} src={previewImage} />
			</Modal>
		</>
	);
}

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default withUserType(connect(mapStateToProps)(AssetAuditReadOnlyModal as any) as any);
